import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { COLORS, S, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API = getBaseURL();

function WinnerControlInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  // States
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLB, setLoadingLB] = useState(false);

  // Ghost form
  const [ghostName, setGhostName] = useState("");
  const [ghostPoints, setGhostPoints] = useState("");
  const [adding, setAdding] = useState(false);

  // Load matches on mount
  useEffect(() => {
    axios
      .get(`${API}/admin/matches/live-upcoming`, { headers })
      .then((r) => setMatches(r.data || []))
      .catch(() => toast.error("Failed to load matches"));
  }, []);

  // Load contests when match selected
  useEffect(() => {
    if (!selectedMatch) { setContests([]); setSelectedContest(null); return; }
    axios
      .get(`${API}/admin/winner-control/contests/${selectedMatch}`, { headers })
      .then((r) => setContests(r.data || []))
      .catch(() => toast.error("Failed to load contests"));
  }, [selectedMatch]);

  // Load leaderboard when contest selected
  useEffect(() => {
    if (!selectedContest) { setLeaderboard([]); return; }
    fetchLeaderboard();
  }, [selectedContest]);

  const fetchLeaderboard = async () => {
    setLoadingLB(true);
    try {
      const r = await axios.get(`${API}/admin/winner-control/leaderboard/${selectedContest}`, { headers });
      setLeaderboard(r.data || []);
    } catch {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLB(false);
    }
  };

  const handleAddGhost = async () => {
    if (!selectedContest) return toast.error("Please select a contest first");
    if (!ghostName.trim()) return toast.error("Enter ghost player name");
    if (!ghostPoints || isNaN(Number(ghostPoints))) return toast.error("Enter valid points");

    setAdding(true);
    try {
      await axios.post(
        `${API}/admin/winner-control/ghost/add`,
        { contestId: selectedContest, name: ghostName.trim(), points: Number(ghostPoints) },
        { headers }
      );
      toast.success(`👻 "${ghostName}" injected at ${ghostPoints} pts!`);
      setGhostName("");
      setGhostPoints("");
      fetchLeaderboard(); // Refresh
    } catch {
      toast.error("Failed to inject ghost");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveGhost = async (ghostId) => {
    if (!selectedContest) return;
    try {
      await axios.delete(`${API}/admin/winner-control/ghost/${selectedContest}/${ghostId}`, { headers });
      toast.success("Ghost removed ✅");
      fetchLeaderboard();
    } catch {
      toast.error("Failed to remove ghost");
    }
  };

  const selectedMatchName = matches.find(m => (m.match_id || m.id) === selectedMatch);
  const selectedContestName = contests.find(c => String(c.id) === String(selectedContest));

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>

      {/* Header */}
      <button style={S.backBtn} onClick={() => navigate("/winrox11-super-admin/dashboard")}>
        ← Back to Dashboard
      </button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>🎭 Ghost Winner System</div>
          <div style={S.pageSubtitle}>Invisible Rank Control · Zero Database · Admin Only</div>
        </div>
        <div style={{
          background: "#a855f715",
          border: "1px solid #a855f730",
          borderRadius: "12px",
          padding: "10px 20px",
          color: "#a855f7",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "1px"
        }}>
          🔒 CONFIDENTIAL
        </div>
      </div>

      {/* How it works */}
      <div style={{ ...S.card, background: "#0a0f1c", border: "1px solid #6366f130", marginBottom: "28px" }}>
        <div style={{ display: "flex", gap: "32px", flexWrap: "wrap" }}>
          {[
            { icon: "1️⃣", title: "Match Select Karo", desc: "Niche se live/upcoming match chuno" },
            { icon: "2️⃣", title: "Contest Chuno", desc: "Us match ka contest select karo" },
            { icon: "3️⃣", title: "Ghost Add Karo", desc: "Naam aur points dalo — database touch nahi hoga" },
            { icon: "4️⃣", title: "Auto Rank Fix", desc: "Users ko real player dikhega, prize release pe koi payment nahi" },
          ].map((step) => (
            <div key={step.icon} style={{ flex: "1 1 180px" }}>
              <div style={{ fontSize: "22px", marginBottom: "6px" }}>{step.icon}</div>
              <div style={{ fontSize: "13px", fontWeight: 700, color: COLORS.text, marginBottom: "4px" }}>{step.title}</div>
              <div style={{ fontSize: "11px", color: COLORS.textMuted }}>{step.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Select Match + Contest */}
      <div style={S.card}>
        <div style={S.cardTitle}>Step 1 — Match & Contest Select Karo</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          <div>
            <label style={S.label}>Match</label>
            <select
              style={S.select}
              value={selectedMatch || ""}
              onChange={(e) => setSelectedMatch(e.target.value || null)}
            >
              <option value="">— Select Match —</option>
              {matches.map((m) => (
                <option key={m.match_id || m.id} value={m.match_id || m.id}>
                  {m.team1} vs {m.team2}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={S.label}>Contest</label>
            <select
              style={S.select}
              value={selectedContest || ""}
              onChange={(e) => setSelectedContest(e.target.value || null)}
              disabled={!selectedMatch}
            >
              <option value="">— Select Contest —</option>
              {contests.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title} · ₹{c.entry_fee} Entry · {c.joined_spots}/{c.total_spots} Joined
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Step 2: Add Ghost */}
      {selectedContest && (
        <div style={S.card}>
          <div style={S.cardTitle}>Step 2 — Ghost Winner Inject Karo</div>
          <div style={{ fontSize: "12px", color: COLORS.textMuted, marginBottom: "20px" }}>
            Ye player database mein nahi jayega. Sirf leaderboard pe dikhega jab tak aap remove na karo.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: "16px", alignItems: "flex-end" }}>
            <div>
              <label style={S.label}>Ghost Player Name</label>
              <input
                style={S.input}
                className="admin-input-focus"
                placeholder="e.g. King_Virat_18"
                value={ghostName}
                onChange={(e) => setGhostName(e.target.value)}
              />
            </div>
            <div>
              <label style={S.label}>Points</label>
              <input
                style={S.input}
                className="admin-input-focus"
                type="number"
                placeholder="e.g. 285.5"
                value={ghostPoints}
                onChange={(e) => setGhostPoints(e.target.value)}
              />
            </div>
            <button
              style={{ ...S.btnPrimary, background: "#a855f7", padding: "12px 24px", whiteSpace: "nowrap" }}
              onClick={handleAddGhost}
              disabled={adding}
              className="admin-btn-hover"
            >
              {adding ? "Injecting..." : "👻 Inject Ghost"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Live Leaderboard Preview */}
      {selectedContest && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div style={S.cardTitle}>
              Step 3 — Live Leaderboard Preview
              {selectedContestName && (
                <span style={{ fontWeight: 400, color: COLORS.textMuted, fontSize: "11px", marginLeft: "10px" }}>
                  ({selectedContestName.title})
                </span>
              )}
            </div>
            <button
              style={{ ...S.btnPrimary, padding: "8px 18px", fontSize: "11px" }}
              onClick={fetchLeaderboard}
              className="admin-btn-hover"
            >
              🔄 Refresh
            </button>
          </div>

          {loadingLB ? (
            <div style={S.loading}>Loading leaderboard...</div>
          ) : leaderboard.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>🏏</div>
              <div>Koi bhi user abhi tak is contest mein join nahi hua</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                    {["Rank", "Player Name", "Points", "Mobile", "Type", "Action"].map((h) => (
                      <th key={h} style={{
                        padding: "10px 14px",
                        fontSize: "10px",
                        color: COLORS.textMuted,
                        fontWeight: 700,
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        textAlign: "left",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row, idx) => (
                    <tr
                      key={row.team_id}
                      className="admin-row-hover"
                      style={{
                        borderBottom: `1px solid ${COLORS.cardBorder}`,
                        background: row.is_ghost ? "#a855f708" : "transparent",
                      }}
                    >
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{
                          fontWeight: 800,
                          fontSize: "16px",
                          color: row.rank === 1 ? "#f59e0b" : row.rank === 2 ? "#94a3b8" : row.rank === 3 ? "#b45309" : COLORS.textMid
                        }}>
                          #{row.rank}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 600, color: COLORS.text, display: "flex", alignItems: "center", gap: "8px" }}>
                          {row.is_ghost && <span style={{ fontSize: "14px" }}>👻</span>}
                          {row.name}
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontWeight: 700, color: COLORS.indigoLight }}>{row.total_points} pts</span>
                      </td>
                      <td style={{ padding: "12px 14px", color: COLORS.textMuted, fontSize: "13px" }}>
                        {row.mobile}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {row.is_ghost ? (
                          <span style={S.badge("#a855f7")}>Ghost</span>
                        ) : (
                          <span style={S.badge(COLORS.green)}>Real</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {row.is_ghost && (
                          <button
                            style={{ ...S.btnDanger, padding: "6px 14px", fontSize: "11px" }}
                            onClick={() => handleRemoveGhost(row.ghost_id)}
                            className="admin-btn-hover"
                          >
                            🗑 Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Warning */}
      <div style={{
        background: "#ef444410",
        border: "1px solid #ef444430",
        borderRadius: "12px",
        padding: "16px 20px",
        color: "#ef4444",
        fontSize: "12px",
        fontWeight: 600,
        display: "flex",
        gap: "10px",
        alignItems: "flex-start"
      }}>
        <span style={{ fontSize: "16px" }}>⚠️</span>
        <div>
          <div style={{ marginBottom: "4px" }}>Important: Ghost entries sirf frontend display ke liye hain.</div>
          <div style={{ fontWeight: 400, color: COLORS.textMuted }}>
            Prize release karte waqt ghost users ko koi real payment nahi jayegi — ye entries sirf leaderboard pe dikhti hain. 
            Database mein koi bhi change nahi hoga.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WinnerControl() {
  return (
    <ToastProvider>
      <WinnerControlInner />
    </ToastProvider>
  );
}
